import { useCallback } from "react";
import { SkyLink, SkyButton } from "@skypointcloud/skypoint-ui";
import { useNavigate, useLocation } from "react-router";
import { viewProfileHref } from "../../../routes/employeePaths";
import { Menu } from "lucide-react";
import { useMsalAuth } from "../../../hooks/useMsalAuth";
import { useSidebarOptional } from "../../../contexts/SidebarContext";
import { TenantLogo, useHeaderLogoHome } from "./TenantLogo";
// Import extracted hooks
import { useHeaderData, useMobileDetection, useCurrentUser } from "./hooks";
import { useProductionSwitchPrompt } from "./useProductionSwitchPrompt";

// Import extracted components
import { ProfileMenu, NonProductionWarningModal, ProductSwitcher } from "./components";
// skyEmployee's search and bell; they render only on skyEmployee pages.
import { NotificationBell } from "../../employee/notifications/NotificationBell";
import { HeaderSearch } from "../../employee/search/HeaderSearch";
import { useDispatch } from "react-redux";
import { persistor } from "../../../store";
import { clearTenantsData } from "../../../store/slices/tenantsSlice";
import { NewVersionNotice } from "../../../components/shared/NewVersionNotice";

const HeaderSky = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getQueryString } = useMsalAuth();
  const sidebar = useSidebarOptional();
  const dispatch = useDispatch();

  // Use extracted hooks
  const isMobileDevice = useMobileDetection();
  const { selectedInstanceId } = useHeaderData();

  // Sandbox→Production prompt: only when Production is reachable; Stay Here
  // lasts the session (#1262).
  const { currentUser } = useCurrentUser();
  const {
    show: showWarning,
    selectedInstance,
    productionInstance,
    stayHere,
  } = useProductionSwitchPrompt({ selectedInstanceId, email: currentUser?.email });

  const redirectToInstance = async (instanceId: string) => {
    dispatch(clearTenantsData());
    await persistor.flush();
    const arr = window.location.href.split("/");
    window.location.href = `${arr[0]}//${arr[2]}?instanceid=${instanceId}`;
  };

  // Inside skyEmployee "View profile" is the skyEmployee profile (in the view
  // you're in); elsewhere it's /account. /account is module-neutral, so carry
  // the module we're in (#1046). Without it the shell derives the product from
  // the path alone and "View profile" silently switched you to Service Desk —
  // header pill, sidebar and all. The module comes from where you ARE, not the
  // path: a shared page (Users & access, skyAgent, /account itself) opened from
  // Workforce or Employee Experience has no module in its path.
  const handleViewAccount = useCallback(() => {
    navigate(viewProfileHref(location.pathname, location.search, getQueryString()));
  }, [navigate, getQueryString, location.pathname, location.search]);

  const logoHome = useHeaderLogoHome();

  const renderDesktopLogo = () => {
    if (isMobileDevice) return null;

    return (
      <SkyLink
        href={logoHome + "?instanceid=" + selectedInstanceId}
        className="bg-[#242424] h-[50px] border-r-0 p-2 flex items-center"
      >
        <TenantLogo />
      </SkyLink>
    );
  };

  return (
    <>
      {showWarning && productionInstance && selectedInstance && (
        <NonProductionWarningModal
          currentInstanceName={selectedInstance.displayname}
          productionInstanceName={productionInstance.displayname}
          onSwitch={() => redirectToInstance(productionInstance.id)}
          onDismiss={stayHere}
        />
      )}
      <div className="min-w-full fixed top-0 left-0 z-999">
        {/* Above the header, moving it and the page down while it shows. */}
        <NewVersionNotice />
        {/* Desktop Header - visible at md (992px+) per Bootstrap breakpoints */}
        <div
          role="toolbar"
          aria-label="Header toolbar"
          className="hidden! md:flex! bg-[#242424] h-[52px] justify-between px-4 py-2 z-10000 text-white shadow-md"
        >
          <div className="flex items-center">{renderDesktopLogo()}</div>
          <div className="flex items-center gap-3">
            <ProductSwitcher />
            <HeaderSearch />
            <NotificationBell />
            <ProfileMenu
              onViewAccount={handleViewAccount}
              onSignOut={logout}
            />
          </div>
        </div>

        {/* Mobile Header - visible below md (992px) per Bootstrap breakpoints */}
        <div className="md:hidden!">
          <div
            role="toolbar"
            aria-label="Mobile header toolbar"
            className="bg-[#242424] h-14 flex items-center justify-between px-3 z-10000 text-white"
          >
            <div className="flex items-center gap-3">
              <SkyButton
                icon={Menu}
                appearance="transparent"
                className="text-white! min-w-10! h-10!"
                aria-label="Open menu"
                onClick={() => sidebar?.setIsCollapsed(false)}
              />
              <SkyLink
                href={logoHome + "?instanceid=" + selectedInstanceId}
                className="flex items-center"
              >
                <TenantLogo heightClass="h-7" />
              </SkyLink>
            </div>
            <div className="flex items-center gap-2">
              <HeaderSearch />
              <NotificationBell />
              <ProfileMenu
                onViewAccount={handleViewAccount}
                onSignOut={logout}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeaderSky;
